/**
 * Judge.me UX enhancements (theme layer only):
 * Write-a-review form as a fixed-position animated popup (no page CLS).
 */
(function () {
  "use strict";

  var ROOT_ID = "judgeme_product_reviews";
  var CLOSE_BTN_CLASS = "jm-aqua-form-close";
  var OPEN_CLASS = "jm-aqua-form-open";
  var SETUP_ATTR = "data-jm-aqua-ux";
  var ANIM_MS = 280;
  var lockedScrollY = 0;
  var closing = false;
  var opening = false;

  function getRoot() {
    return document.getElementById(ROOT_ID);
  }

  function inlineDisplay(el) {
    if (!el) return "";
    var style = (el.getAttribute("style") || "").toLowerCase().replace(/\s+/g, "");
    var match = style.match(/display:([a-z-]+)/);
    return match ? match[1] : "";
  }

  function isFormOpen(formWrapper, root) {
    if (!formWrapper) return false;
    if (root.classList.contains(OPEN_CLASS)) return true;
    var display = inlineDisplay(formWrapper);
    if (display === "none") return false;
    if (display === "block" || display === "flex") return true;
    var writeLink = root.querySelector(".jdgm-write-rev-link");
    return !!(writeLink && writeLink.getAttribute("aria-expanded") === "true");
  }

  function scrollbarWidth() {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  function lockPageScroll() {
    if (document.documentElement.classList.contains("jm-aqua-modal-lock")) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.setProperty("--jm-sbw", scrollbarWidth() + "px");
    document.documentElement.classList.add("jm-aqua-modal-lock");
    document.body.style.top = "-" + lockedScrollY + "px";
  }

  function unlockPageScroll() {
    if (!document.documentElement.classList.contains("jm-aqua-modal-lock")) return;
    document.documentElement.classList.remove("jm-aqua-modal-lock");
    document.documentElement.style.removeProperty("--jm-sbw");
    document.body.style.top = "";
    window.scrollTo(0, lockedScrollY);
  }

  function ensureCloseButton(formWrapper) {
    if (!formWrapper || formWrapper.querySelector("." + CLOSE_BTN_CLASS)) return;

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = CLOSE_BTN_CLASS;
    btn.setAttribute("aria-label", "Close review form");
    btn.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

    var form = formWrapper.querySelector(".jdgm-form");
    if (form) form.prepend(btn);
    else formWrapper.prepend(btn);
  }

  function prepareFormOverlay(formWrapper) {
    if (!formWrapper) return;
    formWrapper.style.position = "fixed";
    formWrapper.style.inset = "0";
    formWrapper.style.zIndex = "99999";
    formWrapper.style.margin = "0";
    formWrapper.style.width = "100%";
    formWrapper.style.height = "100%";
    formWrapper.style.boxSizing = "border-box";
  }

  function showFormOverlay(formWrapper) {
    prepareFormOverlay(formWrapper);
    formWrapper.style.display = "flex";
  }

  function hideFormOverlay(formWrapper) {
    if (!formWrapper) return;
    formWrapper.style.display = "none";
  }

  function setModalOpen(root) {
    var formWrapper = root.querySelector(".jdgm-form-wrapper");
    if (!formWrapper) return;

    if (opening) return;
    opening = true;
    closing = false;
    ensureCloseButton(formWrapper);
    showFormOverlay(formWrapper);
    lockPageScroll();

    if (!root.classList.contains(OPEN_CLASS)) {
      root.classList.remove(OPEN_CLASS);
      void formWrapper.offsetWidth;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          root.classList.add(OPEN_CLASS);
          opening = false;
        });
      });
    } else {
      root.classList.add(OPEN_CLASS);
      opening = false;
    }
  }

  function openForm(root) {
    var writeLink = root.querySelector(".jdgm-write-rev-link");
    if (!root.querySelector(".jdgm-form-wrapper")) return;
    if (writeLink) writeLink.setAttribute("aria-expanded", "true");
    setModalOpen(root);
  }

  function closeForm(root, options) {
    var opts = options || {};
    var formWrapper = root.querySelector(".jdgm-form-wrapper");
    var writeLink = root.querySelector(".jdgm-write-rev-link");
    var cancel = root.querySelector(".jdgm-cancel-rev");

    if (closing || !root.classList.contains(OPEN_CLASS)) return;

    closing = true;
    opening = false;
    root.classList.remove(OPEN_CLASS);

    window.setTimeout(function () {
      unlockPageScroll();
      if (!opts.skipCancel && cancel) {
        cancel.click();
      } else if (formWrapper) {
        hideFormOverlay(formWrapper);
      }
      if (writeLink) writeLink.setAttribute("aria-expanded", "false");
      closing = false;
    }, ANIM_MS);
  }

  function isSubmitControl(el) {
    if (!el) return false;
    var btn = el.closest(
      ".jdgm-submit-rev, .jdgm-btn--solid, button[type='submit'], input[type='submit']"
    );
    if (!btn) return false;
    /* Only treat solid buttons inside the review form as submit */
    if (btn.classList.contains("jdgm-btn--solid") && !btn.closest(".jdgm-form")) {
      return false;
    }
    if (btn.closest(".jdgm-cancel-rev")) return false;
    return !!btn.closest(".jdgm-form-wrapper, .jdgm-form");
  }

  function hasSubmitSuccess(root) {
    return !!(
      root.querySelector(".jdgm-form__success, .jdgm-notify--success, .jdgm-thank-you, .jdgm-submitted") ||
      root.querySelector(".jdgm-form.jdgm--done, .jdgm-form-wrapper.jdgm--done")
    );
  }

  function hasSubmitError(root) {
    return !!(
      root.querySelector(
        ".jdgm-form__error, .jdgm-error, .jdgm-notify--error, .jdgm-form .jdgm-input-error, .jdgm-form [aria-invalid='true']"
      )
    );
  }

  function closeAfterSubmit(root) {
    var formWrapper = root.querySelector(".jdgm-form-wrapper");
    var attempts = 0;
    var maxAttempts = 24; /* ~6s */

    function finishClose() {
      if (!root.classList.contains(OPEN_CLASS) || closing) return;
      closeForm(root, { skipCancel: true });
    }

    function tryClose() {
      attempts += 1;

      if (!root.classList.contains(OPEN_CLASS) || closing) return;

      /* Keep popup open while Judge.me shows validation errors */
      if (hasSubmitError(root)) return;

      if (
        hasSubmitSuccess(root) ||
        inlineDisplay(formWrapper) === "none"
      ) {
        finishClose();
        return;
      }

      /* After submit has had time to process, close unless errors appeared */
      if (attempts >= 3) {
        finishClose();
        return;
      }

      if (attempts < maxAttempts) {
        window.setTimeout(tryClose, 250);
      }
    }

    window.setTimeout(tryClose, 350);
  }

  function bindModal(root) {
    if (root.getAttribute(SETUP_ATTR + "-modal") === "1") return;
    root.setAttribute(SETUP_ATTR + "-modal", "1");

    root.addEventListener(
      "click",
      function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;

        if (target.closest("." + CLOSE_BTN_CLASS)) {
          event.preventDefault();
          event.stopPropagation();
          closeForm(root);
          return;
        }

        if (
          target.classList.contains("jdgm-form-wrapper") &&
          root.classList.contains(OPEN_CLASS)
        ) {
          closeForm(root);
          return;
        }

        if (target.closest(".jdgm-write-rev-link")) {
          var writeCta = target.closest(".jdgm-write-rev-link");
          /* Refresh CTA should not open the write-review popup */
          if (writeCta.classList.contains("jdgm-write-rev-link--refresh")) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          openForm(root);
          return;
        }

        if (target.closest(".jdgm-cancel-rev")) {
          event.preventDefault();
          event.stopPropagation();
          closeForm(root);
          return;
        }

        if (isSubmitControl(target) && root.classList.contains(OPEN_CLASS)) {
          /* Let Judge.me handle submit; close once it completes */
          closeAfterSubmit(root);
        }
      },
      true
    );

    root.addEventListener(
      "submit",
      function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest(".jdgm-form, .jdgm-form-wrapper")) return;
        if (!root.classList.contains(OPEN_CLASS)) return;
        closeAfterSubmit(root);
      },
      true
    );

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      if (!root.classList.contains(OPEN_CLASS)) return;
      closeForm(root);
    });

    var formWrapper = root.querySelector(".jdgm-form-wrapper");
    if (formWrapper && typeof MutationObserver === "function") {
      var mo = new MutationObserver(function () {
        var display = inlineDisplay(formWrapper);
        if (display === "block" || display === "flex") {
          if (!root.classList.contains(OPEN_CLASS) && !closing && !opening) {
            showFormOverlay(formWrapper);
            setModalOpen(root);
          } else {
            prepareFormOverlay(formWrapper);
          }
        } else if (display === "none" && root.classList.contains(OPEN_CLASS) && !closing) {
          /* Judge.me hid the form after submit — close our overlay too */
          closeForm(root, { skipCancel: true });
        }
      });
      mo.observe(formWrapper, {
        attributes: true,
        attributeFilter: ["style", "class", "hidden"],
        subtree: true,
        childList: true,
      });
    }
  }

  function formatAverageOneDecimal(value) {
    var n = parseFloat(String(value).replace(/[^\d.-]/g, ""), 10);
    if (isNaN(n)) return "";
    return (Math.ceil(n * 10) / 10).toFixed(1);
  }

  function syncSummaryAverage(root) {
    var widg = root.querySelector(".jdgm-rev-widg");
    var averageEl = root.querySelector(".jdgm-rev-widg__summary-average");
    if (!averageEl) return false;

    var raw =
      (widg && widg.getAttribute("data-average-rating")) ||
      (averageEl.textContent || "").trim();
    if (!raw) return false;

    var formatted = formatAverageOneDecimal(raw);
    if (!formatted) return false;

    if (averageEl.textContent.trim() !== formatted) {
      averageEl.textContent = formatted;
    }
    return true;
  }

  function syncSummaryReviewCount(root) {
    var widg = root.querySelector(".jdgm-rev-widg");
    var summaryText = root.querySelector(".jdgm-rev-widg__summary-text");
    if (!widg || !summaryText) return false;

    var count = widg.getAttribute("data-number-of-reviews");
    if (!count) return false;

    if (summaryText.getAttribute("data-number-of-reviews") !== count) {
      summaryText.setAttribute("data-number-of-reviews", count);
    }
    return true;
  }

  function bindSummaryReviewCount(root) {
    syncSummaryReviewCount(root);
    syncSummaryAverage(root);

    if (root.getAttribute(SETUP_ATTR + "-review-count") === "1") return;
    root.setAttribute(SETUP_ATTR + "-review-count", "1");

    if (typeof MutationObserver !== "function") return;

    var mo = new MutationObserver(function () {
      syncSummaryReviewCount(root);
      syncSummaryAverage(root);
    });
    mo.observe(root, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["data-number-of-reviews", "data-average-rating"],
    });
  }

  function enhance(root) {
    if (!root) return;
    var formWrapper = root.querySelector(".jdgm-form-wrapper");
    if (formWrapper) {
      prepareFormOverlay(formWrapper);
      if (!isFormOpen(formWrapper, root)) {
        hideFormOverlay(formWrapper);
      }
    }
    bindModal(root);
    bindSummaryReviewCount(root);
    root.setAttribute(SETUP_ATTR, "1");
  }

  function tryBind() {
    var root = getRoot();
    if (!root) return false;
    if (
      !root.querySelector(".jdgm-write-rev-link") &&
      !root.querySelector(".jdgm-form-wrapper") &&
      !root.querySelector(".jdgm-rev-widg")
    ) {
      return false;
    }
    enhance(root);
    return true;
  }

  function getStickyHeaderOffset() {
    var stickyHeader = document.querySelector("hdt-sticky-header");
    if (stickyHeader) {
      var height = stickyHeader.getBoundingClientRect().height;
      if (height > 0) return height;
    }
    var cssHeight = getComputedStyle(document.documentElement)
      .getPropertyValue("--header-height")
      .trim();
    var parsed = parseFloat(cssHeight);
    return isNaN(parsed) ? 72 : parsed;
  }

  function scrollToReviewsWithHeaderOffset() {
    var target = getRoot();
    if (!target) return;
    var offset = getStickyHeaderOffset();
    var top =
      target.getBoundingClientRect().top +
      (window.pageYOffset || document.documentElement.scrollTop) -
      offset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  function bindPreviewBadgeScroll() {
    if (document.documentElement.getAttribute(SETUP_ATTR + "-badge-scroll") === "1") {
      return;
    }
    document.documentElement.setAttribute(SETUP_ATTR + "-badge-scroll", "1");

    document.addEventListener(
      "click",
      function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;

        var badge = target.closest(
          ".hdt-main-product .pdp-card-product-review, .hdt-main-product .plp-card-product-review"
        );
        if (!badge) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        scrollToReviewsWithHeaderOffset();
      },
      true
    );
  }

  function init() {
    bindPreviewBadgeScroll();
    var attempts = 0;
    function tick() {
      attempts += 1;
      var root = getRoot();
      if (root) {
        if (
          root.querySelector(".jdgm-write-rev-link") ||
          root.querySelector(".jdgm-form-wrapper") ||
          root.querySelector(".jdgm-rev-widg")
        ) {
          enhance(root);
        }
        syncSummaryReviewCount(root);
        syncSummaryAverage(root);
      }
      if (attempts > 60) window.clearInterval(timer);
    }
    var timer = window.setInterval(tick, 250);
    tick();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
